/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { secondsToTimeString, unixEpochToString } from "../../util/DateTime";
import SplitField from "../../provisioning/elements/SplitField";
import ItemLabel from "../../util/Icons";
import CaptivePortalPreview from "../captivePortal/CaptivePortalPreview";
import ItemLink from "../../elements/ItemLink";
import ActionButton from "../../elements/ActionButton";
import ItemAttribute from "../../elements/ItemAttribute";
import CenterModal from "../../elements/CenterModal";
import { Label } from "reactstrap";
import { Suspense } from "react";

const ModalItemPreview = ({
  type,
  item,
  isOpen,
  onDone,
}) => {
  // data map
  const typeInfoMap = {
    "client": {
      contact: true,
      clickConfig: { type: "client" },
      fields: [ "locations", "routers", "created-at", "last-guest-activity" ]
    },
    "location": {
      contact: true,
      clickConfig: null,
      fields: [ "client", "routers", "created-at", "last-guest-activity" ]
    },
    "router": {
      contact: false,
      clickConfig: { type: "router" },
      fields: [ "client", "location", "created-at", "last-guest-activity" ]
    },
    "account": {
      contact: false,
      clickConfig: { type: "account" },
      fields: [ "client", "email", "created-at" ]
    },
    "template": {
      contact: false,
      clickConfig: { type: "template" },
      fields: [ "client", "created-at" ]
    },
    "captive-portal": {
      contact: false,
      clickConfig: null,
      fields: null
    },
    "marketing": {
      contact: false,
      clickConfig: { type: "marketing" },
      fields: [ "client", "created-at", "marketing-type", "marketing-medium", ]
    },
  };
  const typeInfo = typeInfoMap[type];

  // validate info
  if (typeInfo == null) {
    return null;
  }

  // booleans
  const isRouter = type === "router";
  const isCaptivePortal = type === "captive-portal";

  // helpers
  const title = isRouter ? item.macAddress : item.name;

  return (
    <CenterModal isOpen={isOpen} toggle={onDone} size={isCaptivePortal ? "md" : "lg"}>
      <div className="d-flex justify-content-between">
        <div style={{ width: "28px" }}>
          { /* TODO: Add contact info here */ }
        </div>
        {typeInfo.clickConfig ? (
          <ItemLink
            className="pb-2 text-center fw-bold"
            tag="h2"
            type={typeInfo.clickConfig.type}
            item={item}
            onClick={onDone}
            showIcon
          >
            {title}
          </ItemLink>
        ) : (
          <ItemLabel className="pb-2 text-center fw-bold" tag="h2" type={type}>{title}</ItemLabel>
        )}
        <ActionButton type="close" onClick={onDone} />
      </div>
      {isCaptivePortal ? (
        <>
          <Label className="w-100 text-center" tag="h5">
            {item.clientName ? (
              <span>
                From business: <i className="bi-briefcase-fill" /> <span className="fw-bold">{item.clientName}</span>
              </span>
            ) : (
              <span className="fw-bold">
                <i className="bi-globe2" /> Global Layout
              </span>
            )}
          </Label>
          <Suspense>
            <CaptivePortalPreview config={item.config} />
          </Suspense>
          <ItemLink className="pt-2 w-100 text-center" type="captive-portal" item={item} onClick={onDone}>
            Edit Layout
          </ItemLink>
        </>
      ) : (
        <SplitField>
          {typeInfo.fields.map((field, index) => {
            const props = {};
            switch (field) {
              case "client":
                props.title = "Business";
                props.icon = "briefcase-fill";
                props.value = item.clientName;
                props.type = "client";
                props.item = { id: item.clientId, name: item.clientName };
                props.onClick = onDone ;
                break;
              case "location":
                props.title = "Location";
                props.icon = "building-fill";
                props.value = item.locationName;
                break;
              case "locations":
                props.title = "Locations";
                props.icon = "building-fill";
                props.value = item.locations?.length;
                break;
              case "router":
                props.title = "Router";
                props.icon = "router-fill";
                props.value = item.macAddress ?? item.name;
                break;
              case "routers":
                props.title = "Routers";
                props.icon = "router-fill";
                props.value = item.routers?.length;
                break;
              case "email":
                props.title = "Email";
                props.icon = "envelope-fill";
                props.value = item.email;
                break;
              case "marketing-type":
                props.title = "Marketing Type";
                props.icon = "megaphone-fill";
                switch (item.marketingType) {
                  case "broadcast": props.value = "Broadcast"; break;
                  case "automation": props.value = "Automation"; break;
                  default: props.value = "Unknown"; break;
                }
                break;
              case "marketing-medium":
                props.title = "Message Medium";
                props.icon = "send-fill"
                switch (item.medium) {
                  case "email": props.value = "Email"; break;
                  case "sms": props.value = "SMS"; break;
                  default: props.value = "Unknown"; break;
                }
                break;
              case "created-at":
                props.title = "Created At";
                props.icon = "clock-fill";
                props.value = unixEpochToString(item.createdAt);
                break;
              case "last-guest-activity":
                props.title = "Last Guest Activity";
                props.icon = "person-fill";
                props.value = secondsToTimeString(item.lastActive, " ago");
                break;
              default: return null;
            }

            props.text = props.value ?? "Unknown";
            return <ItemAttribute key={index} {...props} />;
          })}
        </SplitField>
      )}
    </CenterModal>
  );
};

export default ModalItemPreview;
